/* eslint-disable jsx-a11y/anchor-is-valid */

/**
 * Libs
 */
import Link from 'next/link';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Constants
 */
import { RouteE } from 'src/constants/pages.constants';

/**
 * Components
 */
import { ExploreSearch } from 'src/features/common/explore/components/explore-search/explore-search';

/**
 * Hooks
 */
import {
    useClearItems,
    useFilters,
    useSetFilters,
    useSetSorting,
    useSorting,
    useFetchLaunchpads,
} from 'src/features/common/explore/hooks/use-explore-launchpads';
import { useLaunchpadSortingOptions } from 'src/features/common/explore/hooks/use-launchpad-sorting-options';
import { ExploreSortingModal } from 'src/features/common/explore/modals/explore-sorting-modal';

/**
 * Styles
 */
import {
    StyledContainer,
    StyledMobileActions,
} from 'src/features/common/launchpad/containers/profile-launchpads-search-container/styles';
import { useProfile } from 'src/features/common/profile/hooks/profile.hooks';
import { usePublicProfile } from 'src/features/common/profile/hooks/public-profile.hooks';
import { Button } from 'src/ui/common/atoms/button';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { Visible } from 'src/ui/common/layouts/visible';
import { Select } from 'src/ui/common/organisms/select';

export const ProfileLaunchpadsSearchContainer = () => {
    const { t } = useTranslation(['components', 'actions']);
    const profile = useProfile();
    const publicProfile = usePublicProfile();
    const filters = useFilters();
    const setFilters = useSetFilters();
    const sorting = useSorting();
    const setSorting = useSetSorting();
    const sortingOptions = useLaunchpadSortingOptions();
    const fetchLaunchpads = useFetchLaunchpads();
    const clearItems = useClearItems();

    const profileOwner = profile?.user?.login && publicProfile && publicProfile.login === profile?.user?.login;

    const selectedSorting = useMemo(
        // @ts-ignore
        () => sortingOptions.find((el) => el.value === sorting),
        [sorting, sortingOptions]
    );

    const onSortChange = useCallback(
        async (option) => {
            if (sorting !== option.value) {
                setSorting(option.value);
                clearItems();

                await fetchLaunchpads();
            }
        },
        [clearItems, fetchLaunchpads, setSorting, sorting]
    );

    const openSortingModal = useCallback(() => {
        ExploreSortingModal.open({
            onApply: onSortChange,
            // @ts-ignore
            sorting,
            // @ts-ignore
            sortingOptions,
        });
    }, [onSortChange, sorting, sortingOptions]);

    useEffect(() => {
        fetchLaunchpads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    return (
        <Spacing mb="20px">
            <StyledContainer flexDirection="column" flexDirectionSm="row">
                <Spacing mrSm="10px" mb="5px" mbSm="0">
                    {/* @ts-ignore */}
                    <ExploreSearch
                        filters={filters}
                        // @ts-ignore
                        setFilters={setFilters}
                        placeholder={t('search.profileLaunchpadsPlaceholder', { ns: 'components' })}
                    />
                </Spacing>
                <Visible view={['laptop', 'desktop']}>
                    <Spacing mr="10px">
                        <Select
                            value={selectedSorting}
                            options={sortingOptions}
                            onChange={onSortChange}
                            fitParentWidth
                        />
                    </Spacing>
                </Visible>
                <StyledMobileActions view={['mobile']}>
                    <FlexBox>
                        <Button variant="grey" disabled stretch>
                            {t('misc.filters', { ns: 'actions' })}
                        </Button>
                        <Button variant="grey" onClick={openSortingModal} stretch>
                            {t('exploreFilter.actions.sorting.label', { ns: 'components' })}
                        </Button>
                    </FlexBox>
                </StyledMobileActions>
                {!!profileOwner && (
                    <Spacing mt="5px" mtSm="0">
                        <Link href={RouteE.LAUNCHPAD_CREATE}>
                            <a>
                                <Button variant="white" stretch>
                                    {t('misc.launchpadCreate', { ns: 'actions' })}
                                </Button>
                            </a>
                        </Link>
                    </Spacing>
                )}
            </StyledContainer>
        </Spacing>
    );
};
